import { PaperAirplaneIcon } from "@heroicons/react/24/solid";
import { useEffect, useRef, useState } from "react";
import styles from "./ProviderChatInput.module.css";

interface ProviderChatInputProps {
	onSendMessage: (content: string) => void;
	onTyping?: (isTyping: boolean) => void;
	disabled?: boolean;
	placeholder?: string;
}

export function ProviderChatInput({
	onSendMessage,
	onTyping,
	disabled = false,
	placeholder = "Type a message...",
}: ProviderChatInputProps) {
	const [message, setMessage] = useState("");
	const [isTyping, setIsTyping] = useState(false);
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const typingTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

	// Auto-resize textarea
	useEffect(() => {
		if (textareaRef.current) {
			textareaRef.current.style.height = "auto";
			textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
		}
	}, []);

	// Handle typing indicators
	useEffect(() => {
		if (onTyping) {
			onTyping(isTyping);
		}
	}, [isTyping, onTyping]);

	const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const value = e.target.value;
		setMessage(value);

		// Typing indicator logic
		if (value.trim() && !isTyping) {
			setIsTyping(true);
		}

		// Clear existing timeout
		if (typingTimeoutRef.current) {
			clearTimeout(typingTimeoutRef.current);
		}

		// Set new timeout to stop typing indicator
		typingTimeoutRef.current = setTimeout(() => {
			setIsTyping(false);
		}, 1000);
	};

	const handleSendMessage = () => {
		const content = message.trim();
		if (!content || disabled) return;

		onSendMessage(content);
		setMessage("");
		setIsTyping(false);

		// Clear typing timeout
		if (typingTimeoutRef.current) {
			clearTimeout(typingTimeoutRef.current);
		}

		// Reset textarea height
		if (textareaRef.current) {
			textareaRef.current.style.height = "auto";
		}
	};

	const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSendMessage();
		}
	};

	return (
		<div className={styles.container}>
			<div className={styles.inputWrapper}>
				<div className={styles.textareaContainer}>
					<textarea
						ref={textareaRef}
						value={message}
						onChange={handleInputChange}
						onKeyPress={handleKeyPress}
						placeholder={placeholder}
						disabled={disabled}
						rows={1}
						className={styles.textarea}
					/>
				</div>
				<button
					type="button"
					onClick={handleSendMessage}
					disabled={!message.trim() || disabled}
					className={styles.sendButton}
				>
					<PaperAirplaneIcon className={styles.sendIcon} />
				</button>
			</div>
		</div>
	);
}
